import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
	setAccessTokenResolver,
	setAuthRefreshHandler,
	setOnAccessBlocked,
	setOnAuthFailure,
} from '@/services/apiClient';
import { queryClient } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import { STORAGE_KEYS } from '@/constants';
import { authApi } from '../api/authApi';
import { useAccessBlockedStore } from './accessBlockedStore';
import type { AuthUser } from '../types';

interface SessionPayload {
	user: AuthUser | null;
	accessToken: string;
	refreshToken: string;
}

interface AuthState {
	user: AuthUser | null;
	accessToken: string | null;
	refreshToken: string | null;
	isAuthenticated: boolean;
	setSession: (payload: SessionPayload) => void;
	clearSession: () => void;
}

/**
 * Drop every cached CMS query. All CMS data belongs to the signed-in user and
 * their tenant, so nothing may outlive the session or show to the next account.
 * The customer portal keeps its cache: it has its own, independent session.
 */
export function removeCmsQueries(): void {
	queryClient.removeQueries({
		predicate: (query) => query.queryKey[0] !== queryKeys.portal.all[0],
	});
}

/**
 * Global auth state. Kept intentionally small; server data belongs to
 * TanStack Query, not the store.
 */
export const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			user: null,
			accessToken: null,
			refreshToken: null,
			isAuthenticated: false,
			setSession: ({ user, accessToken, refreshToken }) =>
				set({ user, accessToken, refreshToken, isAuthenticated: true }),
			clearSession: () => {
				removeCmsQueries();
				// A new sign-in starts unblocked; the backend decides again.
				useAccessBlockedStore.getState().reset();
				set({
					user: null,
					accessToken: null,
					refreshToken: null,
					isAuthenticated: false,
				});
			},
		}),
		{
			name: STORAGE_KEYS.session,
			partialize: (state) => ({
				user: state.user,
				accessToken: state.accessToken,
				refreshToken: state.refreshToken,
				isAuthenticated: state.isAuthenticated,
			}),
		},
	),
);

// Let the shared HTTP client read the current token without importing the store.
setAccessTokenResolver(() => useAuthStore.getState().accessToken);

/**
 * Renew the access token on a 401. Uses the stored refresh token and updates the
 * session; returns `null` when renewal is impossible so the client can clear the
 * session (the route guard then redirects to login).
 */
setAuthRefreshHandler(async () => {
	const { refreshToken, user } = useAuthStore.getState();
	if (!refreshToken) return null;

	try {
		const tokens = await authApi.refresh(refreshToken);
		useAuthStore.getState().setSession({
			user,
			accessToken: tokens.accessToken,
			refreshToken: tokens.refreshToken ?? refreshToken,
		});
		return tokens.accessToken;
	} catch {
		return null;
	}
});

// Session is unrecoverable: drop local state; `RequireAuth` redirects to login.
setOnAuthFailure(() => {
	useAuthStore.getState().clearSession();
});

// The tenant's IP allowlist refused a request: keep the session, show the
// access-denied page (`RequireAuth`) until the user retries or signs out.
setOnAccessBlocked((error) => {
	if (useAuthStore.getState().isAuthenticated) {
		useAccessBlockedStore.getState().block(error);
	}
});
